import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import { useConfigurationStore } from '@/store/configurationStore';
import { RemunerationPeriodicity } from '@/types/remuneration';

import type {
	CompanyNeed,
	CompanyNeedDetailsResponse,
} from '@/modules/companyNeed/types';
import type { CompanyNeedAddress } from '@/modules/companyNeed/types/address';
import type { IHabilitationDocument } from '@/types/documents';
import type {
	Remuneration,
	RemunerationPeriodicityOptionUi,
} from '@/types/remuneration';

const mapHabilitationIdToDocument = (
	habilitationId: string,
	documentTypeRefs: IHabilitationDocument[]
): IHabilitationDocument => {
	const match = documentTypeRefs.find(
		(option) => option.value === habilitationId
	);
	return {
		value: habilitationId,
		label: match?.label ?? '',
		type: match?.type ?? '',
		duration: match?.duration ?? 0,
	};
};

const EMPTY_ADDRESS: CompanyNeedAddress = {
	street: '',
	city: '',
	zipCode: '',
	department: '',
	region: '',
	latitude: 0,
	longitude: 0,
};

const mapDetailsAddress = (
	address: CompanyNeedAddress | null | undefined
): CompanyNeedAddress => ({
	...EMPTY_ADDRESS,
	street: address?.street ?? '',
	city: address?.city ?? '',
	zipCode: address?.zipCode ?? '',
	department: address?.department ?? '',
	region: address?.region ?? '',
	latitude: address?.latitude ?? 0,
	longitude: address?.longitude ?? 0,
	complement: address?.complement ?? undefined,
	country: address?.country ?? undefined,
});

const mapRemuneration = (
	payFrequencyId: string | null,
	salaryFrom: number | null,
	salaryTo: number | null,
	periodicityOptions: RemunerationPeriodicityOptionUi[]
): Remuneration | undefined => {
	if (!payFrequencyId || !salaryFrom || !salaryTo) return undefined;
	return {
		periodicity:
			periodicityOptions.find((option) => option.id === payFrequencyId)
				?.value ?? RemunerationPeriodicity.HOURLY,
		minimumAmount: salaryFrom,
		maximumAmount: salaryTo,
	};
};

export const mapCompanyNeedDetailsResponseToCompanyNeed = (
	data: CompanyNeedDetailsResponse
): CompanyNeed => {
	const configStore = useConfigurationStore();

	return {
		_id: data.id,
		name: data.name,
		priority: data.priority,
		companyId: data.companyId,
		companyName: data.companyName,
		jobId: data.jobId ?? '',
		address: mapDetailsAddress(data.address),
		missions: data.missionsCount,
		poolProfileNumber: data.poolProfileNumber,
		refusedProfileNumber: data.refusedProfileNumber,
		ATSProfileNumber: data.atsProfileNumber,
		positionsNumber: data.positionsNumber ?? 0,
		jobOffer: data.jobOffer ?? undefined,
		startDate: data.startDate ?? undefined,
		endDate: data.endDate ?? undefined,
		createdAt: data.createdAt ?? undefined,
		experienceId: normalizeJobExperience(data.experience),
		contractType: data.contractType ?? undefined,
		description: data.description ?? undefined,
		internalNotes: data.internalNotes ?? undefined,
		habilitations:
			data.habilitations?.map((id) =>
				mapHabilitationIdToDocument(id, configStore.documentTypeRefsOptions)
			) ?? undefined,
		remuneration: mapRemuneration(
			data.payFrequencyId,
			data.salaryFrom,
			data.salaryTo,
			configStore.remunerationPeriodicityOptions
		),
		periodPreferences: data.periodPreferences,
		weekEndRequired: data.weekEndRequired,
		hasEmbedding: data.hasEmbedding,
		canDelete: data.canDelete,

		// agencyId: '',
		// agencyName: '',
	};
};
